import { Core, Position } from "cytoscape";

import { GraphLayout, GraphSummary } from "../../api";

/**
 * Checks if a node has been manually repositioned
 * Compares current position to originalPosition to detect manual moves
 */
export const hasBeenManuallyPositioned = (
  node: cytoscape.NodeSingular,
): boolean => {
  const originalPosition = node.data("originalPosition") as
    | Position
    | undefined;
  if (
    !originalPosition ||
    (originalPosition.x === 0 && originalPosition.y === 0)
  ) {
    return false;
  } else {
    return true;
  }
};

/**
 * Applies the appropriate cytoscape layout based on the graph's format setting
 *
 * Nodes that have been manually positioned will maintain their positions even when an automatic layout is applied.
 *
 * @param cytoscapeInstance - Cytoscape instance
 * @param format - The layout format to apply
 */
export const applyLayoutFormat = (
  cytoscapeInstance: Core,
  format: GraphSummary["format"],
): void => {
  try {
    const manualNodes = cytoscapeInstance
      .nodes()
      .filter((n) => hasBeenManuallyPositioned(n));

    let layout;

    switch (format) {
      case GraphLayout.CIRCLE:
        layout = cytoscapeInstance.layout({
          fit: true,
          name: "circle",
          padding: 20,
          spacingFactor: 1.5,
        });
        break;

      case GraphLayout.FCOSE:
        layout = cytoscapeInstance.layout({
          animate: "end",
          animationDuration: 300,
          edgeElasticity: 0.45,
          fit: true,
          gravity: 0.25,
          gravityCompound: 1.0,
          gravityRange: 3.8,
          gravityRangeCompound: 1.5,
          idealEdgeLength: 150,
          name: "fcose",
          nestingFactor: 0.1,
          nodeOverlap: 20,
          numIter: 2500,
          padding: 20,
          tile: true,
          tilingPaddingHorizontal: 10,
          tilingPaddingVertical: 10,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any);
        break;

      case GraphLayout.HORIZONTALTREE:
        layout = cytoscapeInstance.layout({
          // @ts-expect-error wrong elk options
          elk: {
            algorithm: "layered",
            "elk.direction": "RIGHT",
            "elk.layered.spacing.nodeNodeBetweenLayers": "100",
            "elk.spacing.nodeNode": "50",
          },
          fit: true,
          name: "elk",
          padding: 20,
        });
        break;

      case GraphLayout.MANUAL:
        return;

      case GraphLayout.VERTICALTREE:
        layout = cytoscapeInstance.layout({
          // @ts-expect-error wrong elk options
          elk: {
            algorithm: "layered",
            "elk.direction": "DOWN",
            "elk.layered.spacing.nodeNodeBetweenLayers": "100",
            "elk.spacing.nodeNode": "50",
          },
          fit: true,
          name: "elk",
          padding: 20,
        });
        break;

      default:
        console.warn(`Unknown layout format: ${format}`);
        return;
    }

    if (!layout) return;

    // Lock manually positioned nodes so the layout won't move them
    manualNodes.lock();

    layout.on("layoutstop", () => {
      // Unlock after layout completes so the user can drag them again
      manualNodes.unlock();

      manualNodes.trigger("position");
    });

    layout.run();
  } catch (error) {
    console.error("Error applying layout format", error);
  }
};
